import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import styles from './styles.module.css';
import LeasingSelectYear from '../../../mechanization/leasing/Leasingselect';
import { FertilizerWarehouseResponse } from '../api/types';

ChartJS.register(ArcElement, Tooltip, Legend);

interface DinamictableProps {
  fertilizersWarehouseData: FertilizerWarehouseResponse | undefined;
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
}

export const Dinamictable: React.FC<DinamictableProps> = ({
  fertilizersWarehouseData,
  selectedYear,
  onYearChange,
}) => {
  if (!fertilizersWarehouseData?.res) {
    return null;
  }

  const { totalFertilizerAmount, totalWarehouses, fertilizers, warehouses } = fertilizersWarehouseData.res;

  const fertilizerColors = {
    'Аммиачная селитра': 'rgba(12, 142, 12, 1)',
    'Карбамид (мочевина)': 'rgba(182, 212, 52, 1)',
    'Сульфат аммония': 'rgba(255, 181, 71, 1)',
    'Суперфосфат простой': 'rgba(255, 139, 71, 1)',
    'Суперфосфат двойной': 'rgba(212, 160, 111, 1)',
    'Фосфоритная мука': 'rgba(126, 178, 221, 1)',
    'Калимагнезия': 'rgba(93, 149, 234, 1)',
    '12%': 'rgba(75, 105, 255, 1)',
    'Калий хлористый': 'rgba(60, 120, 255, 1)',
    'Калий сульфат': 'rgba(45, 90, 255, 1)'
  };

  const regionColors = {
    'Чуйская область': 'rgba(12, 142, 12, 1)',
    'Ошская область': 'rgba(182, 212, 52, 1)',
    'Джалал-Абадская область': 'rgba(255, 181, 71, 1)',
    'Баткенская область': 'rgba(255, 139, 71, 1)',
    'Нарынская область': 'rgba(212, 160, 111, 1)',
    'Таласская область': 'rgba(126, 178, 221, 1)',
    'Иссык-Кульская область': 'rgba(93, 149, 234, 1)',
    'Ак-Булун': 'rgba(60, 122, 255, 1)'
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 800,
      easing: 'easeInOutCubic',
    },
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          padding: 20,
          font: {
            size: 14,
            family: "'Roboto', sans-serif",
          },
          generateLabels: (chart: any) => {
            const data = chart.data;
            return data.labels.map((label: string, index: number) => ({
              text: `${label} (${data.datasets[0].data[index]}%)`,
              fillStyle: data.datasets[0].backgroundColor[index],
              strokeStyle: data.datasets[0].backgroundColor[index],
              lineWidth: 0,
              hidden: false,
              index: index,
            }));
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        titleFont: {
          size: 14,
          weight: 'bold',
          family: "'Roboto', sans-serif",
        },
        bodyColor: '#666',
        bodyFont: {
          size: 13,
          family: "'Roboto', sans-serif",
        },
        padding: 12,
        boxPadding: 6,
        borderColor: 'rgba(0,0,0,0.1)',
        borderWidth: 1,
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            return ` ${value.toFixed(1)}%`;
          },
        },
      },
    },
  } as const;

  const fertilizersData = {
    labels: fertilizers.map((fertilizer) => fertilizer.fertilizerTypeRu),
    datasets: [
      {
        data: fertilizers.map((fertilizer) => fertilizer.percentage),
        backgroundColor: fertilizers.map(
          (fertilizer) => fertilizerColors[fertilizer.fertilizerTypeRu as keyof typeof fertilizerColors]
        ),
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  const warehousesData = {
    labels: warehouses.map((warehouse) => warehouse.regionNameRu),
    datasets: [
      {
        data: warehouses.map((warehouse) => warehouse.percentage),
        backgroundColor: warehouses.map(
          (warehouse) => regionColors[warehouse.regionNameRu as keyof typeof regionColors]
        ),
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  return (
    <div className={styles.container}>
      <div className={styles.yearSelector}>
        <LeasingSelectYear value={selectedYear} onChange={onYearChange} />
      </div>
      
      <div className={styles.chartContainer}>
        <div className={styles.chartContent}>
          <div className={styles.chartBox}>
            <h3 className={styles.chartTitle}>
            Диаграмма объемов удобрений по видам
            </h3>
            <div className={styles.chartWrapper}>
              <Doughnut data={fertilizersData} options={chartOptions} />
              <div className={styles.centerText}>
                <div className={styles.centerValue}>
                  {totalFertilizerAmount.toLocaleString()}
                </div>
                <div className={styles.centerLabel}>тонн</div>
              </div>
            </div>
          </div>

          <div className={styles.chartBox}>
            <h3 className={styles.chartTitle}>
            Диаграмма по количеству складов
            </h3>
            <div className={styles.chartWrapper}>
              <Doughnut data={warehousesData} options={chartOptions} />
              <div className={styles.centerText}>
                <div className={styles.centerValue}>
                  {totalWarehouses.toLocaleString()}
                </div>
                <div className={styles.centerLabel}>складов</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};